import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ChangePasswordSectionProps {
  email: string;
}

export default function ChangePasswordSection({ email }: ChangePasswordSectionProps) {
  const { t } = useTranslation("profile");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ current?: string; new?: string; confirm?: string }>({});

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (newPassword.length < 8) next.new = t("security.minLength");
    if (newPassword !== confirmPassword) next.confirm = t("security.mismatch");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setSaving(true);
    try {
      // Step 1: verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (signInError) {
        setErrors({ current: t("security.wrongCurrent") });
        setSaving(false);
        return;
      }

      // Step 2: update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updateError) {
        toast.error(t("security.error") + ": " + updateError.message);
        setSaving(false);
        return;
      }

      toast.success(t("security.success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      toast.error(t("security.error"));
    }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-lg">{t("security.title")}</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">{t("security.description")}</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current password */}
          <div className="space-y-2">
            <Label htmlFor="current-password">{t("security.currentPassword")}</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setErrors((p) => ({ ...p, current: undefined })); }}
              aria-describedby={errors.current ? "current-password-error" : undefined}
              aria-invalid={!!errors.current}
            />
            {errors.current && (
              <p id="current-password-error" className="text-xs text-destructive">{errors.current}</p>
            )}
          </div>

          {/* New password */}
          <div className="space-y-2">
            <Label htmlFor="new-password">{t("security.newPassword")}</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setErrors((p) => ({ ...p, new: undefined })); }}
              aria-describedby={errors.new ? "new-password-error" : undefined}
              aria-invalid={!!errors.new}
            />
            {errors.new && (
              <p id="new-password-error" className="text-xs text-destructive">{errors.new}</p>
            )}
          </div>

          {/* Confirm new password */}
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t("security.confirmPassword")}</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirm: undefined })); }}
              aria-describedby={errors.confirm ? "confirm-password-error" : undefined}
              aria-invalid={!!errors.confirm}
            />
            {errors.confirm && (
              <p id="confirm-password-error" className="text-xs text-destructive">{errors.confirm}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={saving || !currentPassword || !newPassword || !confirmPassword}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t("security.changePassword")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
